import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Container,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import heroSrc from "@/assets/images/eb-banner.jpg";
import { AuthApiService } from "@/services";
import { LoginRequest } from "@/types";
import logo from "../assets/images/eb_logo.png";
import {API_CONFIG, API_ENDPOINTS} from "@/constants/apiConfig";
const SignIn: React.FC = () => {
  /**
   * Password requirements configuration
   * @constant {Object} PASSWORD_REQUIREMENTS
   * @property {number} minLength - Minimum password length
   * @property {boolean} requireLowercase - Whether lowercase letters are required
   * @property {boolean} requireNumbers - Whether numbers are required
   * @property {boolean} requireSpecialChars - Whether special characters are required
   */
  const PASSWORD_REQUIREMENTS = {
    minLength: 7,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
  } as const;

  /**
   * State for managing form data and UI state
   * @type {Object}
   * @property {boolean} showPassword - Toggle password visibility
   * @property {Object} formData - Form field values
   * @property {string} formData.email - User's email address
   * @property {string} formData.password - User's password
   * @property {Object} errors - Form validation errors
   * @property {string} loginError - Error message for login failures
   * @property {boolean} isLoading - Loading state for form submission
   * @property {boolean} showSuccess - Whether to show success message
   * @property {string} successMessage - Success message to display
   */
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginRequest>({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState<Partial<LoginRequest>>({});
  const [loginError, setLoginError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  /**
   * Handles changes to form input fields
   * @function handleInputChange
   * @param {React.ChangeEvent<HTMLInputElement>} event - The input change event
   * @returns {void}
   * @description
   * - Updates the form data with the new input value
   * - Clears any existing error for the changed field
   */
  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name as keyof LoginRequest]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  /**
   * Validates a password against complexity requirements
   * @function validatePasswordComplexity
   * @param {string} password - The password to validate
   * @returns {string | null} Error message if validation fails, null if valid
   * @description
   * Checks the password against the following requirements:
   * - Minimum length
   * - At least one lowercase letter
   * - At least one number
   * - At least one special character
   */
  const validatePasswordComplexity = (password: string): string | null => {
    if (!password) return "Password is required";

    if (password.length < PASSWORD_REQUIREMENTS.minLength) {
      return `Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters long`;
    }

    if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
      return "Must contain at least one lowercase letter";
    }

    if (PASSWORD_REQUIREMENTS.requireNumbers && !/\d/.test(password)) {
      return "Must contain at least one number";
    }

    if (
      PASSWORD_REQUIREMENTS.requireSpecialChars &&
      !/[^A-Za-z0-9]/.test(password)
    ) {
      return "Must contain at least one special character";
    }

    return null;
  };

  /**
   * Validates all form fields and sets error messages
   * @function validateForm
   * @returns {boolean} True if all validations pass, false otherwise
   * @description
   * - Validates email format
   * - Validates password complexity
   * - Sets error messages in the errors state
   */
  const validateForm = (): boolean => {
    const newErrors: Partial<LoginRequest> = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    const passwordError = validatePasswordComplexity(formData.password);
    if (passwordError) {
      newErrors.password = passwordError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handles form submission and authentication
   * @async
   * @function handleSubmit
   * @param {React.FormEvent<HTMLFormElement>} event - The form submission event
   * @returns {Promise<void>}
   * @description
   * - Prevents default form submission
   * - Validates form data
   * - Checks against static credentials
   * - Handles success and error states
   * - Redirects to dashboard on successful login
   */
const handleSubmit = async (
  event: React.FormEvent<HTMLFormElement>
): Promise<void> => {
  event.preventDefault();

  if (!validateForm()) {
    return;
  }

  setLoginError("");
  setIsLoading(true);

  try {
    const response = await axios.post(
  `${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH.LOGIN}`,
  {
    email: formData.email,
    password: formData.password,
  }
);

    console.log("Login Response:", response.data);

    // Adjust based on your API response
    const token = response.data.token;
    const userName = response.data.user_name || response.data.name;

    localStorage.setItem("auth_token", token);
    localStorage.setItem("user_name", userName);

    setSuccessMessage("Login successful! Redirecting to dashboard...");
    setShowSuccess(true);

    setTimeout(() => {
      navigate("/dashboard");
    }, 2000);
  } catch (error: any) {
    console.error("Login Error:", error);

    if (error.response) {
      setLoginError(
        error.response.data?.message ||
          "Invalid email or password"
      );
    } else {
      setLoginError("Unable to connect to server");
    }
  } finally {
    setIsLoading(false);
  }
};
 
  /**
   * Handles changes to the password input field
   * @function handlePasswordChange
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
   * @returns {void}
   */
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    // Update the form data
    setFormData((prev) => ({
      ...prev,
      password: value,
    }));

    // Always validate on change
    const passwordError = validatePasswordComplexity(value);
    if (passwordError) {
      setErrors((prev) => ({
        ...prev,
        password: passwordError,
      }));
    } else {
      // Clear error if validation passes
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.password;
        return newErrors;
      });
    }
  };
  if (showSuccess) {
    return (
      <Container
        component="main"
        maxWidth="sm"
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            p: 4,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: "#4caf50", // Green color for success
              fontWeight: 500,
              mb: 3,
            }}
          >
            ✅ Login Successful!
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "text.primary",
              mb: 2,
            }}
          >
            {successMessage}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
            }}
          >
            Welcome to EB Industries
          </Typography>
        </Box>
      </Container>
    );
  }
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f3f6f9",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "63%",
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: "#ffff",
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", width: "100%",padding:'8px 20px' }}>
          {/* LEFT: Image + blue header */}
          <Box
            sx={{
              width: { xs: "100%", md: "60%" },
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Blue header */}
            <Box
              sx={{
                color: "#fff",
                px: 3,
                py: 2.5,
                borderTopLeftRadius: 12,
                borderBottomRightRadius: 0,
              }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ lineHeight: 1, color: "#fff", display: "flex", alignItems: "left" }}
              >
                <img src={logo} style={{ width: "50%" }} />
              </Typography>
            </Box>

            {/* Hero image with text overlay */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: { xs: 260, md: "calc(100% - 64px)" },
              }}
            >
              <Box
                component="img"
                src={heroSrc}
                alt="hero"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  borderBottomLeftRadius: 12,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: "27%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "90%",
                  maxWidth: 1200,
                  display: "flex",
                  flexDirection: { xs: "row", md: "column" },
                  justifyContent: "space-between",
                  gap: { xs: 2, md: 2, lg: 3 },
                  zIndex: 1,
                }}
              >
                {/* How We're Different Card */}
                <Box className="textBox"
                  sx={{
                    // width: { xs: "100%", md: "89%", lg: "54%" },
                    
                    backgroundColor: "#464646D4",
                    borderRadius: "12px",
                    overflow: "hidden",
                    color: "white",
                    textShadow: "2px 2px 4px #464646D4",
                    maxWidth: 700,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "20px",
                      p: 1,
                      color: "#fff",
                      textAlign: "left",
                    }}
                  >
                    How We're Different
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: "#1b184d",
                      borderRadius: "0 0 12px 12px",
                      fontSize:'17px'
                    }}
                  >
                    {[
                      "Service and Speed",
                      "Unmatched Quality",
                      "Business Growth Support",
                    ].map((item, index) => (
                      <Typography
                        key={index}
                        sx={{
                          fontSize:'17px',
                          textAlign: "left",
                          color: "#fff",
                          textShadow: "none",
                          px: 1,
                          py: "6px",
                        }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>

                {/* Our Guiding Principles Card */}
                {/* <Box className="textBox"
                  sx={{
                   
                    backgroundColor: "#464646D4",
                    borderRadius: "12px",
                    overflow: "hidden",
                    color: "white",
                    textShadow: "2px 2px 4px #464646D4",
                    maxWidth: 600,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "20px",
                      p: 1,
                      color: "#fff",
                      textAlign: "left",
                    }}
                  >
                    Our Guiding Principles
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: "#1b184d",
                      borderRadius: "0 0 12px 12px",
                    }}
                  >
                    {[
                      "Intelligent, Customer-Driven Design",
                      "Highest Quality Manufacturing",
                      "Outstanding Customer Service",
                    ].map((item, index) => (
                      <Typography
                        key={index}
                        sx={{
                           fontSize:'17px',
                          textAlign: "left",
                          color: "#fff",
                          textShadow: "none",
                          px: 1,
                          py: "6px",
                        }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box> */}
              </Box>
            </Box>
          </Box>

          {/* RIGHT: Login form */}
          <Box
            sx={{
              width: { xs: "100%", md: "40%" },
              p: { xs: 3, md: 3 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 420,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                margin:'10px'
              }}
            >
              <Box sx={{ flexGrow: 1, mt: 5 }}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ mb: 3, textAlign: "left",fontSize:'20px' }}
                >
                  Login
                </Typography>
                <Box component="form" onSubmit={handleSubmit}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mb: 0.5, textAlign: "left" }}
                      >
                        Email
                      </Typography>
                      <TextField
                        placeholder="Enter your email"
                        fullWidth
                        type="email"
                        name="email"
                        size="small"
                        variant="outlined"
                        value={formData.email}
                        onChange={handleInputChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        disabled={isLoading}
                        sx={{
                          backgroundColor: "#f5f7fb",
                          border:'1px solid #DBDCDE',
                          borderRadius:'8px',
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "1px solid black",
                          },
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mb: 0.5, textAlign: "left" }}
                      >
                        Password
                      </Typography>
                      <Box>
                        <TextField
                          placeholder="Enter your password"
                          fullWidth
                          size="small"
                          type={showPassword ? "text" : "password"}
                          variant="outlined"
                          value={formData.password}
                          onChange={handlePasswordChange}
                          onBlur={() => {
                            if (formData.password) {
                              const passwordError = validatePasswordComplexity(
                                formData.password
                              );
                              setErrors((prev) => ({
                                ...prev,
                                password: passwordError || "",
                              }));
                            }
                          }}
                          error={!!errors.password}
                          disabled={isLoading}
                           InputProps={{
      endAdornment: (
        <IconButton
          edge="end"
          onClick={() => setShowPassword((prev) => !prev)}
          sx={{ color: "#3A3541" }}
        >
          {showPassword ? <VisibilityOff sx={{ color: "#3A3541",fontSize:'18px' }}/>: <Visibility sx={{ color: "#3A3541",fontSize:'18px' }}/>}
        </IconButton>
      ),
      sx: {
        backgroundColor: "#f5f7fb",
         border:'1px solid #DBDCDE',
                          borderRadius:'8px',
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: errors.password ? "#f44336" : "transparent",
        },
      },
    }}
                        />
                        {errors.password && (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        mt: 0.5,
        ml: 1.5,
        fontSize: "0.75rem",
        color: "#f44336",
      }}
    >
      {errors.password}
    </Typography>
  )}
                      </Box>
                    </Box>
                    <Typography
                      style={{
                        color: "#1b184d",
                        fontSize: "12px",
                        textAlign: "right",
                        cursor: "pointer",
                      }}
                      onClick={() => navigate("/forgot-password")}
                    >
                      Forgot password
                    </Typography>

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      sx={{
                        bgcolor: "#1b184d",
                        "&:hover": { bgcolor: "#1b184d" },
                        py: 1.25,
                        textTransform: "none",
                        borderRadius: 1,
                        mt: 1,
                        mb: 1,
                      }}
                    >
                      {isLoading ? "Signing In..." : "Sign In"}
                    </Button>

                    {/* Display login error (e.g., invalid credentials) */}
                    {loginError && (
                      <Box sx={{ mt: 1, mb: 2, width: "100%" }}>
                        <Typography
                          variant="body2"
                          color="error"
                          align="left"
                          sx={{
                            backgroundColor: "#ffebee",
                            p: 1,
                            borderRadius: 1,
                            borderLeft: "4px solid #f44336",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          &bull; {loginError}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Box>
              <Typography
                sx={{
                  textAlign: "center",
                  mt: "auto",
                  mb: 2,
                  fontSize: "11px",
                }}
              >
                Powered by{" "}
                <a
                  href="https://aperturexi.com/"
                  target="_blank"
                  style={{ textDecoration: "underline", color: "#1b184d" }}
                >
                  AXI
                </a>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default SignIn;
